import React, { useContext, useEffect, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Platform, ScrollView, View } from 'react-native';
import * as base64ToBlob from 'b64-to-blob';
import {
  Button, Dialog, Portal,
  IconButton,
  TextInput,
  ProgressBar,
} from 'react-native-paper';
import { getDocumentAsync } from 'expo-document-picker';
import {
  Box, VStack, HStack, Text, Progress, Pressable,
} from 'native-base';
import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import Picker from '../../../components/commons/Picker';
import {
  DeleteFileForUserMutation, UploadFileForUserMutation,
} from '../../../gql/user/mutation';
import {
  FindManyFilesForUserQuery, FindOneFileForUser, DownloadAllFilesForUserAsZip,
  ReviewEligibilityForProfile, FindFilesAvailableForReuse,
} from '../../../gql/file/query';
import { UpdateOneFile, ReuseFilesFromPreviousSeason } from '../../../gql/file/mutation';
import DocumentViewerModal from '../../../modals/DocumentViewer/DocumentViewerModal';
import ConfirmationModal from '../../../modals/Confirmation/ConfirmationModal';
import { ModalContext } from '../../../context/ModalContext';
import { FindOneUserQuery } from '../../../gql/user/query';
import { computeAge, getFileType } from '../../../utils/utils';
import { AuthContext } from '../../../context/AuthContext';
import { ROLE_NAMES } from '../../../utils/constant';
import DocumentEditorModal from '../../../modals/DocumentEditor/DocumentEditorModal';
import RefreshableScreen from '../../Refreshable/RefreshableScreen';

export const DOCUMENT_TYPE = {
  CNI_RECTO: {
    name: 'Carte identité (recto)', color: 'lightyellow', visible: 'always', order: 1,
  },
  CNI_VERSO: {
    name: 'Carte identité (verso)', color: 'lightyellow', visible: 'always', order: 2,
  },
  CNI_RL_RECTO: {
    name: 'Carte identité responsable légal (recto)', color: 'lightyellow', visible: 'isMinor', order: 3,
  },
  CNI_RL_VERSO: {
    name: 'Carte identité responsable légal (verso)', color: 'lightyellow', visible: 'isMinor', order: 4,
  },
  EXTRAIT_LIVRET_FAMILLE: {
    name: 'Extrait livret de famille', color: 'lightyellow', visible: 'isMinor', order: 5,
  },
  EXTRAIT_LIVRET_FAMILLE_RL: {
    name: 'Extrait livret de famille du responsable légal', color: 'lightyellow', visible: 'isMinor', order: 6,
  },
  PASSPORT: {
    name: 'Passport', color: 'lightyellow', visible: 'always', order: 7,
  },
  CARTE_VITALE: {
    name: 'Carte vitale', color: 'lightblue', visible: 'always', order: 8,
  },
  MUTUELLE: {
    name: 'Mutuelle', color: 'lightblue', visible: 'always', order: 9,
  },
  CARTE_VITALE_INTERNATIONALE: {
    name: 'Carte vitale internationale',
    color: 'lightblue',
    visible: 'optional',
    order: 10,
    info: 'Facultatif, sauf pour les compétitions internationales',
  },
  AUTORISATION_SORTIE_TERRITOIRE: {
    name: 'Autorisation sortie territoire',
    color: 'lightyellow',
    visible: 'optional',
    order: 11,
    info: 'Facultatif, sauf pour les compétitions internationales (mineurs uniquement)',
  },
  FICHE_SANITAIRE_LIAISON: {
    name: 'Fiche sanitaire de liaison', color: 'lightblue', visible: 'isMinor', order: 12,
  },
  VACCINS: {
    name: 'Vaccins', color: 'lightblue', visible: 'always', order: 13,
  },
  OBJECTIF_PALMARES: {
    name: 'Objectifs et palmarès compétiteurs', color: 'orange', visible: 'always', order: 14,
  },
  AUTORISATION_PARENTALE: {
    name: 'Autorisation parentale', color: 'orange', visible: 'isMinor', order: 15,
  },
  LICENCE: {
    name: 'Licence sportive', color: 'lightgreen', visible: 'always', order: 16,
  },
  CERTIFICAT_MEDICAL: {
    name: 'Certificat medical', color: 'lightblue', visible: 'none', order: 17,
  },
  AUTRE: {
    name: 'Fiche d\'inscription', color: 'orange', visible: 'always', order: 18,
  },
  CONVENTION_ENGAGEMENT_COMPETITEURS: {
    name: 'Convention d\'engagement des compétiteurs', color: 'orange', visible: 'always', order: 19,
  },
  DRIVER_LICENCE: {
    name: 'Permis de conduire (recto)', color: 'lightgreen', visible: 'driver', order: 20,
  },
  DRIVER_LICENCE_VERSO: {
    name: 'Permis de conduire (verso)', color: 'lightgreen', visible: 'driver', order: 21,
  },
  DRIVER_CNI_RECTO: {
    name: 'Carte identité conducteur (recto)', color: 'lightgreen', visible: 'driver', order: 22,
  },
  DRIVER_CNI_VERSO: {
    name: 'Carte identité conducteur (verso)', color: 'lightgreen', visible: 'driver', order: 23,
  },
  DRIVER_COMMITMENT: {
    name: 'Engagement du conducteur',
    color: 'lightgreen',
    visible: 'driver',
    order: 24,
    info: 'Téléchargez le modèle dans Documents > Général, signez-le et déposez-le ici',
  },
};

export const DRIVER_DOCUMENT_TYPES = Object.entries(DOCUMENT_TYPE)
  .filter((k) => k[1].visible === 'driver').map((k) => k[0]);

function DocumentTab({ user }) {
  const { authState, isSuperAdmin } = useContext(AuthContext);
  const {
    displayDocumentViewerModal,
    toggleDocumentViewerModal,
    displayConfirmationModal,
    toggleConfirmationModal,
    displayDocumentEditorModal,
    toggleDocumentEditorModal,
  } = useContext(ModalContext);
  const { data, loading, refetch } = useQuery(FindManyFilesForUserQuery, {
    variables: {
      email: user.email,
    },
  });
  const files = data?.findManyFilesForUser;
  const isCompanion = user.status === 'COMPANION';
  const isDriver = user.role === 'COACH' || user.role === 'SUPERADMIN' || isCompanion;
  const driverFiles = files?.filter((f) => DRIVER_DOCUMENT_TYPES.includes(f.type)) ?? [];
  const providedDriverCount = DRIVER_DOCUMENT_TYPES
    .filter((t) => driverFiles.some((f) => f.type === t)).length;
  // Collapsed by default once every driver document is provided, open while some are missing.
  const [driverCardExpanded, setDriverCardExpanded] = useState(null);
  const isDriverCardExpanded = driverCardExpanded
    ?? providedDriverCount < DRIVER_DOCUMENT_TYPES.length;
  const isOwner = authState.email === user.email;
  const canSubmitForReview = !isCompanion && isOwner
    && user.status !== 'COMPETITOR' && user.status !== 'REVIEW_PENDING';

  const { data: eligibilityData, refetch: refetchEligibility } = useQuery(
    ReviewEligibilityForProfile,
    {
      variables: { profileId: user.profile?.id },
      skip: !canSubmitForReview || !user.profile?.id,
    },
  );
  const eligibility = eligibilityData?.reviewEligibilityForProfile;

  const {
    data: reuseData, loading: reuseLoading, refetch: refetchFilesAvailableForReuse,
  } = useQuery(
    FindFilesAvailableForReuse,
    {
      variables: { profileId: user.profile?.id },
      skip: !isOwner || isCompanion || !user.profile?.id,
    },
  );
  const filesAvailableForReuse = reuseData?.findFilesAvailableForReuse;

  const [isLoading, setIsLoading] = useState(false);
  const [renameModalVisible, setRenameModalVisible] = useState(false);
  const [fileToRename, setFileToRename] = useState(null);
  const [newFileName, setNewFileName] = useState('');

  const [addFileForUser] = useMutation(UploadFileForUserMutation);
  const [deleteFileForUser] = useMutation(DeleteFileForUserMutation);
  const [updateOneFile] = useMutation(UpdateOneFile);
  const [reuseFilesFromPreviousSeason, { loading: reusingFiles }] = useMutation(
    ReuseFilesFromPreviousSeason,
  );
  const [loadFile] = useLazyQuery(
    FindOneFileForUser,
    {
      fetchPolicy: 'network-only',
    },
  );
  const [loadAllFilesZip] = useLazyQuery(
    DownloadAllFilesForUserAsZip,
    {
      fetchPolicy: 'network-only',
    },
  );

  const [displayFileTypeModal, setDisplayFileTypeModal] = useState(false);
  const [openPicker, setOpenPicker] = useState(false);

  const requiredVisible = ['always'];
  if (computeAge(user.profile?.dateOfBirth) < 18) requiredVisible.push('isMinor');
  const offerableVisible = [...requiredVisible, 'optional'];

  const requiredTypes = Object.entries(DOCUMENT_TYPE)
    .filter((k) => requiredVisible.includes(k[1].visible)).map((k) => k[0]);
  const optionalTypes = Object.entries(DOCUMENT_TYPE)
    .filter((k) => k[1].visible === 'optional').map((k) => k[0]);
  const providedTypes = files?.map((f) => f.type) ?? [];
  const requiredDocumentCount = requiredTypes.length;
  const providedRequiredCount = requiredTypes.filter((t) => providedTypes.includes(t)).length;
  const optionalDocumentCount = optionalTypes.length;
  const providedOptionalCount = optionalTypes.filter((t) => providedTypes.includes(t)).length;

  const fileTypes = Object.entries(DOCUMENT_TYPE)
    .filter((k) => offerableVisible.includes(k[1].visible)
      && !files?.map((f) => f.type).includes(k[0]));
  const [documentType, setDocumentType] = useState(
    fileTypes.length > 0 ? fileTypes[0][0] : undefined,
  );

  const getProgressColorScheme = () => {
    const total = requiredDocumentCount;
    if (total === 0) {
      return 'info';
    }
    const percentage = (providedRequiredCount / total) * 100;
    if (percentage < 33) {
      return 'danger';
    }
    if (percentage < 100) {
      return 'yellow';
    }
    return 'success';
  };

  const handleRenameFile = async () => {
    if (newFileName && fileToRename) {
      await updateOneFile({
        variables: {
          fileId: fileToRename.id,
          name: newFileName,
        },
      });
      setRenameModalVisible(false);
      refetch();
    }
  };

  const viewFile = async (fileId, filename) => {
    setIsLoading(true);
    const fileData = await loadFile({
      variables: {
        email: user.email,
        fileId,
      },
    });
    const base64file = fileData.data.findOneFileForUser.file;
    toggleDocumentViewerModal({
      fileId, filename, isWeb: Platform.OS === 'web', file: base64file,
    });
    setIsLoading(false);
  };

  const editFile = async (fileId, filename) => {
    setIsLoading(true);
    const fileData = await loadFile({
      variables: {
        email: user.email,
        fileId,
      },
    });
    const base64file = fileData.data.findOneFileForUser.file;
    toggleDocumentEditorModal({
      fileId, filename, isWeb: Platform.OS === 'web', file: base64file,
    });
    setIsLoading(false);
  };

  useEffect(() => {
    if (fileTypes.length > 0) setDocumentType(fileTypes[0][0]);
  }, [files]);

  const saveBase64File = async (base64file, filename) => {
    if (Platform.OS === 'web') {
      const type = getFileType(base64file);
      const cleanedBase64 = base64file.split(',')[1];
      // generate a blob, then a file and then save the file.
      const blob = base64ToBlob(cleanedBase64, type);
      const file = new File([blob], filename, { type });

      import('file-saver').then((fs) => {
        fs.default.saveAs(file);
      });
    } else {
      const type = getFileType(base64file);
      const cleanedBase64 = base64file.split(',')[1];

      if (Platform.OS === 'android') {
        try {
          const permissions = await FileSystem.StorageAccessFramework.requestDirectoryPermissionsAsync();
          if (permissions.granted) {
            const fileUri = await FileSystem.StorageAccessFramework.createFileAsync(
              permissions.directoryUri,
              filename,
              type,
            );
            await FileSystem.writeAsStringAsync(fileUri, cleanedBase64, {
              encoding: FileSystem.EncodingType.Base64,
            });
          } else {
            // Handle case where permissions are not granted
            console.log('Permissions not granted for StorageAccessFramework');
          }
        } catch (e) {
          console.error('Error saving file on Android:', e);
        }
      } else if (Platform.OS === 'ios') {
        const fileUri = FileSystem.documentDirectory + filename;
        try {
          await FileSystem.writeAsStringAsync(fileUri, cleanedBase64, {
            encoding: FileSystem.EncodingType.Base64,
          });
          if (await Sharing.isAvailableAsync()) {
            await Sharing.shareAsync(fileUri);
          } else {
            console.log('Sharing is not available on iOS device.');
          }
        } catch (e) {
          console.error('Error saving file on iOS:', e);
        }
      }
    }
  };

  const downloadFile = async (fileId, filename) => {
    setIsLoading(true);
    const fileData = await loadFile({
      variables: {
        email: user.email,
        fileId,
      },
    });
    const base64file = fileData.data.findOneFileForUser.file;
    await saveBase64File(base64file, filename);
    setIsLoading(false);
  };

  const downloadAllFiles = async () => {
    setIsLoading(true);
    try {
      const zipData = await loadAllFilesZip({
        variables: {
          email: user.email,
        },
      });
      const { fileName, file: base64file } = zipData.data.downloadAllFilesForUserAsZip;
      await saveBase64File(base64file, fileName);
    } catch (e) {
      console.error('Error downloading all files as zip:', e);
    }
    setIsLoading(false);
  };

  const uploadFile = async () => {
    const result = await getDocumentAsync({
      type: ['application/pdf', 'image/jpeg', 'application/msword'],
      copyToCacheDirectory: true,
    });
    if (!result.canceled) {
      const asset = result.assets[0];
      let base64File = asset.uri;
      if (Platform.OS !== 'web') {
        // For native platforms, asset.uri is a file path
        base64File = await FileSystem.readAsStringAsync(asset.uri, {
          encoding: FileSystem.EncodingType.Base64,
        });
        // Add the data URL prefix using asset.mimeType
        base64File = `data:${asset.mimeType};base64,${base64File}`;
      }
      await addFileForUser({
        variables: {
          profileId: user.profile.id,
          file: base64File,
          name: asset.name,
          type: documentType,
        },
        refetchQueries: [
          FindOneUserQuery, // DocumentNode object parsed with gql
          'findOneUser', // Query name
        ],
      });
      refetch();
      if (canSubmitForReview) refetchEligibility();

      setDisplayFileTypeModal(false);
    }
  };

  const deleteFile = async (fileId) => {
    if (fileId) {
      await deleteFileForUser({
        variables: {
          profileId: user.profile.id,
          fileId,
        },
      });
      refetch();
      if (canSubmitForReview) refetchEligibility();
    }
  };

  const handleReuseFiles = async () => {
    await reuseFilesFromPreviousSeason({
      variables: {
        profileId: user.profile.id,
        fileIds: filesAvailableForReuse.map((f) => f.id),
      },
    });
    refetch();
    refetchFilesAvailableForReuse();
    if (canSubmitForReview) refetchEligibility();
  };

  const uploadDriverDocument = async (type) => {
    const result = await getDocumentAsync({
      type: ['application/pdf', 'image/jpeg', 'application/msword'],
      copyToCacheDirectory: true,
    });
    if (!result.canceled) {
      const asset = result.assets[0];
      let base64File = asset.uri;
      if (Platform.OS !== 'web') {
        base64File = await FileSystem.readAsStringAsync(asset.uri, {
          encoding: FileSystem.EncodingType.Base64,
        });
        base64File = `data:${asset.mimeType};base64,${base64File}`;
      }
      const existing = driverFiles.find((f) => f.type === type);
      if (existing) {
        await deleteFileForUser({
          variables: { profileId: user.profile.id, fileId: existing.id },
        });
      }
      await addFileForUser({
        variables: {
          profileId: user.profile.id,
          file: base64File,
          name: DOCUMENT_TYPE[type].name,
          type,
        },
      });
      refetch();
    }
  };

  return displayDocumentViewerModal.display || displayDocumentEditorModal.display
    ? (
      <>
        { displayDocumentViewerModal.display && <DocumentViewerModal />}
        { displayDocumentEditorModal.display && <DocumentEditorModal />}
      </>
    )
    : (
      <VStack flex={1} p={4} space={4}>
        {displayConfirmationModal.display && <ConfirmationModal />}
        <Portal>
          <Dialog visible={displayFileTypeModal} onDismiss={() => setDisplayFileTypeModal(false)}>
            <Dialog.Title>Type de document</Dialog.Title>
            <Dialog.Content>
              <View style={{ zIndex: 1000 }}>
                <Picker
                  selectedValue={documentType}
                  onValueChange={(itemValue) => setDocumentType(itemValue)}
                  open={openPicker}
                  setOpen={setOpenPicker}
                  zIndex={1000}
                >
                  {fileTypes.map((k) => <Picker.Item key={k[0]} label={k[1].name} value={k[0]} />)}
                </Picker>
              </View>
              {DOCUMENT_TYPE[documentType]?.info && (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {DOCUMENT_TYPE[documentType].info}
                </Text>
              )}
            </Dialog.Content>
            <Dialog.Actions>
              <Button onPress={() => uploadFile()}>Parcourir</Button>
            </Dialog.Actions>
          </Dialog>
          <Dialog visible={renameModalVisible} onDismiss={() => setRenameModalVisible(false)}>
            <Dialog.Title>Renommer le fichier</Dialog.Title>
            <Dialog.Content>
              <TextInput
                label="Nouveau nom"
                value={newFileName}
                onChangeText={setNewFileName}
              />
            </Dialog.Content>
            <Dialog.Actions>
              <Button onPress={() => setRenameModalVisible(false)}>Annuler</Button>
              <Button onPress={handleRenameFile}>Renommer</Button>
            </Dialog.Actions>
          </Dialog>
        </Portal>

        {loading
          ? <ProgressBar progress={1} indeterminate />
          : (
            <>
              {isLoading && <ProgressBar progress={1} indeterminate />}
              <RefreshableScreen onRefresh={() => refetch()}>

                {/* Driver documents card — shown for drivers (COMPANION, COACH, SUPERADMIN) */}
                {isDriver && (
                  <Box bg="white" p={4} borderRadius="md" shadow={1} mb={4}>
                    <Pressable onPress={() => setDriverCardExpanded(!isDriverCardExpanded)}>
                      <HStack alignItems="center" justifyContent="space-between">
                        <VStack>
                          <Text fontWeight="bold" fontSize="md" color="black">
                            Documents conducteur
                          </Text>
                          <Text
                            fontSize="xs"
                            color={providedDriverCount === DRIVER_DOCUMENT_TYPES.length
                              ? 'green.600' : 'orange.500'}
                          >
                            {`${providedDriverCount} / ${DRIVER_DOCUMENT_TYPES.length} fournis`}
                          </Text>
                        </VStack>
                        <IconButton
                          icon={isDriverCardExpanded ? 'chevron-up' : 'chevron-down'}
                          size={24}
                          onPress={() => setDriverCardExpanded(!isDriverCardExpanded)}
                        />
                      </HStack>
                    </Pressable>
                    {isDriverCardExpanded && (
                      <VStack space={2} mt={2}>
                        {DRIVER_DOCUMENT_TYPES.map((type) => {
                          const file = driverFiles.find((f) => f.type === type);
                          return (
                            <HStack key={type} alignItems="center" justifyContent="space-between">
                              <HStack flex={1} alignItems="center" space={2}>
                                <IconButton
                                  icon={file ? 'check-circle' : 'alert-circle-outline'}
                                  iconColor={file ? 'green' : 'orange'}
                                  size={20}
                                  style={{ margin: 0 }}
                                />
                                <VStack flex={1}>
                                  <Text fontSize="sm" color="black">{DOCUMENT_TYPE[type].name}</Text>
                                  {!file && DOCUMENT_TYPE[type].info && (
                                    <Text fontSize="xs" color="gray.500">
                                      {DOCUMENT_TYPE[type].info}
                                    </Text>
                                  )}
                                </VStack>
                              </HStack>
                              <HStack>
                                <IconButton
                                  icon="upload"
                                  size={22}
                                  onPress={() => uploadDriverDocument(type)}
                                />
                                {file && (
                                  <>
                                    <IconButton
                                      icon="eye-outline"
                                      size={22}
                                      onPress={() => viewFile(file.id, file.name)}
                                    />
                                    <IconButton
                                      icon="delete-outline"
                                      iconColor="red"
                                      size={22}
                                      onPress={() => toggleConfirmationModal({
                                        title: 'Confirmation',
                                        message: 'Voulez-vous vraiment supprimer ce document '
                                          + `(${DOCUMENT_TYPE[type].name}) ?`,
                                        handleSubmit: () => deleteFile(file.id),
                                      })}
                                    />
                                  </>
                                )}
                              </HStack>
                            </HStack>
                          );
                        })}
                      </VStack>
                    )}
                  </Box>
                )}

                {/* Regular documents — hidden for COMPANION users */}
                {!isCompanion && (
                  <>
                    <HStack justifyContent="space-between" alignItems="center" space={4}>
                      <Box flex={1}>
                        <Text fontSize="sm" color="gray.500">
                          {`Obligatoires : ${providedRequiredCount} / ${requiredDocumentCount}`}
                        </Text>
                        <Progress
                          value={Math.round(
                            (providedRequiredCount / (requiredDocumentCount || 1)) * 100,
                          )}
                          colorScheme={getProgressColorScheme()}
                        />
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          {`Facultatifs : ${providedOptionalCount} / ${optionalDocumentCount}`
                            + ' — requis pour les compétitions internationales'}
                        </Text>
                      </Box>
                      <Button
                        disabled={fileTypes.length === 0}
                        onPress={() => setDisplayFileTypeModal(true)}
                        mode="contained"
                      >
                        Ajouter
                      </Button>
                    </HStack>

                    {isSuperAdmin && (
                      <Button
                        icon="folder-zip-outline"
                        mode="outlined"
                        disabled={!files || files.length === 0}
                        onPress={downloadAllFiles}
                      >
                        Télécharger tout (ZIP)
                      </Button>
                    )}

                    {filesAvailableForReuse?.length > 0 && (
                      <Box bg="white" p={4} borderRadius="md" shadow={1}>
                        <VStack space={2}>
                          <Text fontWeight="bold" fontSize="md" color="black">
                            Documents d&apos;une saison précédente disponibles
                          </Text>
                          <Text fontSize="sm" color="gray.500">
                            {`${filesAvailableForReuse.length} document(s) déjà fourni(s) `
                              + 'lors d\'une saison précédente peuvent être réutilisés pour '
                              + 'la saison en cours.'}
                          </Text>
                          <Button
                            mode="contained"
                            icon="autorenew"
                            loading={reusingFiles || reuseLoading}
                            onPress={handleReuseFiles}
                          >
                            Réutiliser ces documents
                          </Button>
                        </VStack>
                      </Box>
                    )}

                    {canSubmitForReview && (
                      <Box bg="white" p={4} borderRadius="md" shadow={1}>
                        <VStack space={2}>
                          <Text fontWeight="bold" fontSize="md" color="black">
                            Suivi du dossier de validation
                          </Text>
                          <Text fontSize="sm" color={eligibility?.documentsComplete ? 'green.600' : 'gray.500'}>
                            {`${eligibility?.documentsComplete ? '✓' : '○'} Documents requis fournis (${eligibility?.providedDocumentCount ?? 0}/${eligibility?.requiredDocumentCount ?? '…'})`}
                          </Text>
                          <Text fontSize="sm" color={eligibility?.contributionValidated ? 'green.600' : 'gray.500'}>
                            {eligibility?.contributionValidated
                              ? '✓ Cotisation encaissée par le club'
                              : '○ Cotisation non encaissée — rapprochez-vous du club'}
                          </Text>
                          <Text fontSize="sm" color={eligibility?.depositValidated ? 'green.600' : 'gray.500'}>
                            {eligibility?.depositValidated
                              ? '✓ Caution équipement encaissée par le club'
                              : '○ Caution équipement non encaissée — rapprochez-vous du club'}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            Dès que toutes les conditions sont remplies, votre dossier
                            est automatiquement soumis pour validation.
                          </Text>
                        </VStack>
                      </Box>
                    )}

                    <ScrollView flex={1}>
                      <VStack space={3}>
                        {files?.filter((f) => !DRIVER_DOCUMENT_TYPES.includes(f.type)).slice()
                          .sort((a, b) => (DOCUMENT_TYPE[a.type].order > DOCUMENT_TYPE[b.type].order ? 1 : -1))
                          .map((f) => (
                            <Box
                              key={f.id}
                              bg="white"
                              p={4}
                              borderRadius="md"
                              shadow={1}
                            >
                              <VStack space={2}>
                                <VStack>
                                  <Text fontWeight="bold" fontSize="md" color="black" isTruncated>
                                    {f.name}
                                  </Text>
                                  <Text fontSize="sm" color="gray.700" mt={1}>
                                    {DOCUMENT_TYPE[f.type].name}
                                  </Text>
                                  {DOCUMENT_TYPE[f.type].info && (
                                    <Text fontSize="xs" color="gray.500">
                                      {DOCUMENT_TYPE[f.type].info}
                                    </Text>
                                  )}
                                </VStack>
                                <HStack space={1} alignSelf="flex-end">
                                  <IconButton
                                    icon="rename-box"
                                    size={24}
                                    onPress={() => {
                                      setFileToRename(f);
                                      setNewFileName(f.name);
                                      setRenameModalVisible(true);
                                    }}
                                  />
                                  <IconButton
                                    icon="eye-outline"
                                    size={24}
                                    onPress={() => viewFile(f.id, f.name)}
                                  />
                                  {(authState.role === ROLE_NAMES.SUPERADMIN || authState.email === user.email)
                            && (
                            <IconButton
                              icon="image-edit-outline"
                              size={24}
                              onPress={() => editFile(f.id, f.name)}
                            />
                            )}
                                  <IconButton
                                    icon="download-outline"
                                    size={24}
                                    onPress={() => downloadFile(f.id, f.name)}
                                  />
                                  <IconButton
                                    icon="delete-outline"
                                    iconColor="red"
                                    size={24}
                                    onPress={() => toggleConfirmationModal({
                                      title: 'Confirmation',
                                      message: 'Voulez-vous vraiment supprimer ce document ?',
                                      handleSubmit: () => deleteFile(f.id),
                                    })}
                                  />
                                </HStack>
                              </VStack>
                            </Box>
                          ))}
                      </VStack>
                    </ScrollView>
                  </>
                )}
              </RefreshableScreen>
            </>
          )}
      </VStack>
    );
}

export default DocumentTab;
